import Modal from "@mui/material/Modal";
import React, {useState} from "react";
import {Box, Button, Checkbox, IconButton, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, Clock01Icon, GitCompareIcon, Undo03Icon} from "@hugeicons/core-free-icons";
import {UI, rgba} from "../../uiTokens";
import {checkboxSx, iconBtnSx, modalCardSx} from "../editMenuStyles";
import {SystemPromptDiffModal} from "./SystemPromptDiffModal";

const formatDate = (ms) => (ms ? new Date(ms).toLocaleString() : "");

// Lighter than primaryBtnSx's solid fill - these buttons sit on every row, a solid purple pill each would be heavy
const tintedBtnSx = {
    textTransform: "none", fontWeight: 600, borderRadius: "8px",
    backgroundColor: rgba(UI.accent, 0.1), color: UI.accent, boxShadow: "none",
    "&:hover": {backgroundColor: rgba(UI.accent, 0.18), boxShadow: "none"},
    "&.Mui-disabled": {backgroundColor: rgba(UI.ink, 0.05), color: UI.muted},
};

// Roughly: is this long enough that the 4-line clamp would actually be cutting it off?
const isLong = (value) => (value || "").length > 240 || (value?.match(/\n/g) || []).length >= 4;

export const SystemPromptHistoryModal = ({open, onClose, history = [], currentValue = "", onRestore}) => {
    const [expanded, setExpanded] = useState(() => new Set());
    const [selected, setSelected] = useState([]);
    const [diffOpen, setDiffOpen] = useState(false);

    const items = [
        {key: "current", label: "Current", value: currentValue, isCurrentRow: true},
        ...history.map((entry, index) => ({
            key: entry.savedAt || `v${index}`,
            label: `${formatDate(entry.savedAt)}${entry.savedBy ? ` · ${entry.savedBy}` : ""}`,
            value: entry.value,
            savedAt: entry.savedAt,
            savedBy: entry.savedBy,
        })),
    ];

    const toggleExpanded = (key) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleSelected = (key) => {
        setSelected(prev => {
            if (prev.includes(key)) return prev.filter(k => k !== key);
            if (prev.length >= 2) return prev;
            return [...prev, key];
        });
    };

    const selectedItems = selected.map(key => items.find(i => i.key === key)).filter(Boolean);

    const close = () => {
        setSelected([]);
        setDiffOpen(false);
        onClose();
    };

    return (
        <>
            <Modal open={open} onClose={close} aria-labelledby="system-prompt-history-title">
                <Box sx={{...modalCardSx, maxWidth: 700}}>
                    <Stack direction="row" gap={1} alignItems="center" sx={{mb: 1}}>
                        <Box sx={{
                            display: "flex", width: 32, height: 32, borderRadius: "8px", alignItems: "center",
                            justifyContent: "center", color: UI.accent, backgroundColor: rgba(UI.accent, 0.12),
                        }}>
                            <HugeiconsIcon icon={Clock01Icon} size={18}/>
                        </Box>
                        <Typography id="system-prompt-history-title" sx={{flex: 1, fontWeight: 700, fontSize: 16, color: UI.ink}}>
                            System Prompt History
                        </Typography>
                        <IconButton size="small" onClick={close} sx={iconBtnSx}>
                            <HugeiconsIcon icon={Cancel01Icon} size={18}/>
                        </IconButton>
                    </Stack>

                    {history.length > 0 && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 1.5}}>
                            <Typography sx={{fontSize: 11, color: UI.muted}}>
                                Select two versions to compare
                            </Typography>
                            <Button
                                size="small" disabled={selected.length !== 2}
                                startIcon={<HugeiconsIcon icon={GitCompareIcon} size={14}/>}
                                sx={{...tintedBtnSx, py: 0.25, px: 1.5, fontSize: 11.5}}
                                onClick={() => setDiffOpen(true)}
                            >
                                Compare
                            </Button>
                        </Stack>
                    )}

                    {history.length === 0 ? (
                        <Typography sx={{fontSize: 12.5, color: UI.muted}}>
                            No saved versions yet — use the save icon next to System Prompt to checkpoint one.
                        </Typography>
                    ) : (
                        <Stack gap={1.5}>
                            {items.map((item) => {
                                const matchesCurrent = !item.isCurrentRow && item.value === currentValue;
                                const isCurrent = item.isCurrentRow || matchesCurrent;
                                const long = isLong(item.value);
                                const isExpanded = expanded.has(item.key);
                                const isChecked = selected.includes(item.key);
                                return (
                                    <Box key={item.key} sx={{
                                        border: `1px solid ${rgba(isCurrent ? UI.accent : UI.ink, isCurrent ? 0.3 : 0.08)}`,
                                        borderRadius: "10px", p: 1.5,
                                        backgroundColor: item.isCurrentRow ? rgba(UI.accent, 0.04) : "transparent",
                                    }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 0.75}}>
                                            <Stack direction="row" gap={0.5} alignItems="center">
                                                <Checkbox
                                                    size="small" sx={{...checkboxSx, p: 0.25}}
                                                    checked={isChecked}
                                                    disabled={!isChecked && selected.length >= 2}
                                                    onChange={() => toggleSelected(item.key)}
                                                />
                                                <Typography sx={{fontSize: 11, fontWeight: 600, color: UI.muted}}>
                                                    {item.isCurrentRow ? "Live value" : item.label}
                                                </Typography>
                                                {item.isCurrentRow && (
                                                    <Typography sx={{
                                                        fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
                                                        color: UI.accent, backgroundColor: rgba(UI.accent, 0.12), borderRadius: "6px", px: 0.75, py: 0.15,
                                                    }}>
                                                        Current
                                                    </Typography>
                                                )}
                                                {matchesCurrent && (
                                                    <Typography sx={{fontSize: 10.5, fontStyle: "italic", color: UI.muted}}>
                                                        Matches current
                                                    </Typography>
                                                )}
                                            </Stack>
                                            {!item.isCurrentRow && (
                                                <Button
                                                    size="small" disabled={isCurrent}
                                                    startIcon={<HugeiconsIcon icon={Undo03Icon} size={14}/>}
                                                    sx={{...tintedBtnSx, py: 0.25, px: 1.5, fontSize: 11.5}}
                                                    onClick={() => onRestore(item.value)}
                                                >
                                                    {isCurrent ? "Restored" : "Restore"}
                                                </Button>
                                            )}
                                        </Stack>
                                        <Typography sx={{
                                            fontSize: 12.5, color: UI.ink, whiteSpace: "pre-wrap",
                                            ...(isExpanded ? {} : {
                                                display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden",
                                            }),
                                        }}>
                                            {item.value || <em>Empty</em>}
                                        </Typography>
                                        {long && (
                                            <Typography
                                                onClick={() => toggleExpanded(item.key)}
                                                sx={{fontSize: 11, fontWeight: 600, color: UI.accent, cursor: "pointer", mt: 0.5, width: "fit-content"}}
                                            >
                                                {isExpanded ? "Show less" : "Show full prompt"}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </Modal>

            <SystemPromptDiffModal
                open={diffOpen}
                onClose={() => setDiffOpen(false)}
                left={selectedItems[0]}
                right={selectedItems[1]}
            />
        </>
    );
};
